import { useCallback } from 'react';
import { getApiUrl } from '../config';

interface UsePlayerSwappingProps {
  matchId: string | undefined;
  onShowNotification: (message: string, type: 'success' | 'error') => void;
  onSyncTeams: () => Promise<void>;
}

interface UsePlayerSwappingReturn {
  swapPlayers: (player1Id: string, player2Id: string) => Promise<void>;
  movePlayer: (playerId: string, teamIndex: number, positionIndex: number) => Promise<void>;
}

export const usePlayerSwapping = ({
  matchId,
  onShowNotification,
  onSyncTeams
}: UsePlayerSwappingProps): UsePlayerSwappingReturn => {
  // API call with authentication
  const apiCallWithAuth = useCallback(async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('authToken');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers
    };

    return fetch(url, { 
      ...options, 
      headers
    });
  }, []);

  // Intercambiar jugadores
  const swapPlayers = useCallback(async (player1Id: string, player2Id: string) => {
    if (!matchId) return;

    try {
      const response = await apiCallWithAuth(getApiUrl(`/api/matches/${matchId}/swap-players`), {
        method: 'POST',
        body: JSON.stringify({
          player1Id,
          player2Id
        })
      });

      if (response.ok) {
        await onSyncTeams();
      } else {
        const error = await response.json();
        onShowNotification(error.error || 'Error intercambiando jugadores', 'error');
        throw new Error(error.error || 'Error intercambiando jugadores');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error swapping players:', error);
      onShowNotification('Error intercambiando jugadores', 'error');
      throw error;
    }
  }, [matchId, apiCallWithAuth, onShowNotification, onSyncTeams]);

  // Mover jugador a posición específica
  const movePlayer = useCallback(async (playerId: string, teamIndex: number, positionIndex: number) => {
    if (!matchId) return;

    try {
      const response = await apiCallWithAuth(getApiUrl(`/api/matches/${matchId}/move-player`), {
        method: 'POST',
        body: JSON.stringify({
          playerId,
          teamIndex,
          positionIndex
        })
      });

      if (response.ok) {
        await onSyncTeams();
      } else {
        const error = await response.json();
        onShowNotification(error.error || 'Error moviendo jugador', 'error');
        throw new Error(error.error || 'Error moviendo jugador');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error moving player:', error);
      onShowNotification('Error moviendo jugador', 'error');
      throw error;
    }
  }, [matchId, apiCallWithAuth, onShowNotification, onSyncTeams]);

  return {
    swapPlayers,
    movePlayer
  };
};
